import type { LinearLightCaptureBounds } from './source';

export type LinearLightBox = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotationAngle?: number;
};

// Map cached page-space paint through the element's own rotated resize axes.
export function getLinearLightGeometry(
  from: LinearLightBox,
  to: LinearLightBox,
  bounds: LinearLightCaptureBounds,
  reflow = false
) {
  // Translation is the hot path; avoid rotated-axis math and corner allocations.
  if (
    from.width === to.width &&
    from.height === to.height &&
    (from.rotationAngle || 0) === (to.rotationAngle || 0)
  ) {
    const dx = to.x - from.x,
      dy = to.y - from.y;
    return {
      transform: `translate(${dx}px, ${dy}px)`,
      bounds: { ...bounds, x: bounds.x + dx, y: bounds.y + dy },
    };
  }
  const oldAngle = ((from.rotationAngle || 0) * Math.PI) / 180;
  const newAngle = ((to.rotationAngle || 0) * Math.PI) / 180;
  const c0 = Math.cos(oldAngle),
    s0 = Math.sin(oldAngle);
  const c1 = Math.cos(newAngle),
    s1 = Math.sin(newAngle);
  const sx = reflow ? 1 : to.width / from.width;
  const sy = reflow ? 1 : to.height / from.height;
  const clean = (value: number) => (Math.abs(value) < 1e-10 ? 0 : value);
  const a = clean(c1 * sx * c0 + s1 * sy * s0);
  const b = clean(s1 * sx * c0 - c1 * sy * s0);
  const c = clean(c1 * sx * s0 - s1 * sy * c0);
  const dd = clean(s1 * sx * s0 + c1 * sy * c0);
  const oldX = from.x + from.width / 2,
    oldY = from.y + from.height / 2;
  // Width reflow retains glyph size and its local top-left until fresh paint arrives.
  const rx = reflow ? (from.width - to.width) / 2 : 0;
  const ry = reflow ? (from.height - to.height) / 2 : 0;
  const newX = to.x + to.width / 2 + c1 * rx - s1 * ry;
  const newY = to.y + to.height / 2 + s1 * rx + c1 * ry;
  const e = clean(newX - a * oldX - c * oldY);
  const f = clean(newY - b * oldX - dd * oldY);
  const x = clean(e + (a - 1) * bounds.x + c * bounds.y);
  const y = clean(f + b * bounds.x + (dd - 1) * bounds.y);
  const corners = [
    [0, 0],
    [bounds.width, 0],
    [0, bounds.height],
    [bounds.width, bounds.height],
  ].map(([dx, dy]) => [
    bounds.x + x + a * dx + c * dy,
    bounds.y + y + b * dx + dd * dy,
  ]);
  const left = Math.min(...corners.map(([px]) => px));
  const top = Math.min(...corners.map(([, py]) => py));
  return {
    transform:
      a === 1 && b === 0 && c === 0 && dd === 1
        ? `translate(${x}px, ${y}px)`
        : `matrix(${a}, ${b}, ${c}, ${dd}, ${x}, ${y})`,
    bounds: {
      x: left,
      y: top,
      width: Math.max(...corners.map(([px]) => px)) - left,
      height: Math.max(...corners.map(([, py]) => py)) - top,
    },
  };
}
